import { useState, useContext } from 'react';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import { useApplyCoupon, useCancelCoupon } from '@/api/coupun';
import { CheckoutContext } from '@/context/CheckoutContext';
import { cn } from '@/utils/cn';
import { Tag } from '@/components/Common/Tag';
import type { PriceBreakdown } from '@/types/checkout';
import SuccessIcon from '@/assets/icons/common/success.svg';
import ErrorIcon from '@/assets/icons/common/error.svg';
import { useTranslate } from '@/hooks/useTranslate';
import { mixpanelService } from '@/services/mixpanelService';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { Templates } from '@/types/common';
import { getErrorMessage } from '@/utils/error';

const calculateDiscountPercentage = (discountAmount: number, totalAmount: number) => {
  const positiveDiscount = Math.abs(discountAmount);
  return (positiveDiscount / totalAmount) * 100;
};

export const PromoCode = () => {
  const context = useContext(CheckoutContext);
  const {
    boot: { couponsConfiguration },
    actions: { updatePriceBreakdown, setPromoCode },
  } = context;
  const [promoCode, setNewPromoCode] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const { mutateAsync: applyCoupon, isPending } = useApplyCoupon();
  const { mutateAsync: cancelCoupon } = useCancelCoupon();
  const { t } = useTranslate();
  const { template } = useContext(CheckoutContext);
  const isOnePayment = template.type === Templates.OnePayment;

  const handleApplyCoupon = async () => {
    if (!promoCode.trim()) return;
    try {
      const priceBreakdown = await applyCoupon(promoCode);
      updatePriceBreakdown(priceBreakdown);
      setSuccess(true);
      setError('');
      const discountPercentage = calculateDiscountPercentage(priceBreakdown?.discount, priceBreakdown?.baseNet);
      setPromoCode({
        name: promoCode,
        discountAmount: priceBreakdown?.discount,
        discountPercentage,
      });

      const properties = getMixpanelProperties(context, {
        promo_code_id: promoCode || undefined,
      });
      mixpanelService.track(MixpanelEvents.COUPON_APPLIED, properties as Record<string, unknown>);
    } catch (error) {
      setSuccess(false);
      setError(getErrorMessage(error, t('couponDoesNotExist')));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPromoCode(e.target.value);
    setSuccess(false);
    setError('');
  };

  const handleClearErrorCoupon = () => {
    setError('');
    setSuccess(false);
    setPromoCode(null);
    setNewPromoCode('');
  };

  const handleRemoveCoupon = async () => {
    try {
      const priceBreakdown = (await cancelCoupon()) as PriceBreakdown;
      updatePriceBreakdown(priceBreakdown);

      const properties = getMixpanelProperties(context, {
        promo_code_id: promoCode || undefined,
      });
      mixpanelService.track(MixpanelEvents.PROMO_CODE_REMOVED, properties as Record<string, unknown>);

      setPromoCode(null);
      setNewPromoCode('');
      setSuccess(false);
      setError('');
    } catch {
      setError(t('failedToRemovePromoCode'));
    }
  };

  return (
    <>
      {couponsConfiguration?.enabled && (
        <div className="flex flex-col gap-1 my-3">
          <div
            className={cn(
              'relative flex items-center rounded-md transition-all duration-200 focus-within:border-muted-foreground',
              success && 'border-success focus-within:border-success',
              error && 'border-danger focus-within:border-danger',
              !success && !error && 'border-base-border'
            )}
          >
            <input
              type="text"
              placeholder={t('promoCode')}
              value={promoCode}
              onChange={handleInputChange}
              className={cn(
                'flex-1 p-2 h-10 pl-3 rounded-md outline-0 text-base md:text-sm font-family-sans appearance-none text-card-foreground bg-white',
                success ? 'pr-32' : promoCode.trim() ? 'pr-20' : 'pr-10',
                isOnePayment && 'border-base-border border-1'
              )}
              disabled={isPending || success}
            />
            <div className="absolute right-4 flex items-center gap-2">
              {success ? (
                <>
                  <div className="cursor-pointer" onClick={handleRemoveCoupon}>
                    <Tag text={t('remove')} />
                  </div>
                  <img src={SuccessIcon} className="w-5 h-5" />
                </>
              ) : error ? (
                <img src={ErrorIcon} className="w-5 h-5" onClick={handleClearErrorCoupon} />
              ) : promoCode.trim() ? (
                <button
                  onClick={handleApplyCoupon}
                  disabled={isPending}
                  className={cn(
                    'text-sm font-medium text-blue transition-opacity duration-200',
                    isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  {t('apply')}
                </button>
              ) : (
                <FontAwesomeIcon icon="fa-regular fa-tag" className="text-muted-foreground" />
              )}
            </div>
          </div>
          {error && <div className="text-xs text-danger">{error}</div>}
        </div>
      )}
    </>
  );
};
